import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import {
  examTypeLabel,
  indicationLabel,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { isWorksheetAllNormal } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { useMemo } from "react";
import { useRxData } from "rxdb-hooks";

export type StudyWorksheet = {
  id: string;
  examTypeLabel: string;
  allNormal: boolean;
  indication: string | null;
  notes: string | null;
};

const summariseWorksheet = (exam: EpocusExam): StudyWorksheet => {
  const data = exam.worksheetData;
  const examType = data?.examType ?? exam.examType;

  return {
    id: exam.id,
    examTypeLabel: examTypeLabel(examType),
    allNormal: data ? isWorksheetAllNormal(data) : false,
    indication: data?.indication
      ? indicationLabel(examType, data.indication)
      : null,
    notes: data?.notes ?? null,
  };
};

export const useStudyWorksheets = (
  accessionNumber: string,
): { worksheets: StudyWorksheet[] } => {
  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: { accessionNumber: { $eq: accessionNumber } },
      }),
  );

  // accessionNumber is unique on both sides of the join, so at most one encounter
  // can match a study.
  const encounterId = encounters[0]?.id;

  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    encounterId
      ? (collection) =>
          collection.find({
            selector: { encounterId },
            sort: [{ id: "asc" }],
          })
      : undefined,
  );

  return useMemo(
    () => ({ worksheets: exams.map(summariseWorksheet) }),
    [exams],
  );
};
